'use client';

import { useState, useEffect } from 'react';
import { CommunityEvent } from '@/types';
import EventCard from './EventCard';
import { Loader2 } from 'lucide-react';

interface UpcomingEventsSectionProps {
  initialEvents?: CommunityEvent[];
}

export default function UpcomingEventsSection({ initialEvents }: UpcomingEventsSectionProps) {
  const [events, setEvents] = useState<CommunityEvent[]>(() => {
    if (!initialEvents) return [];
    return initialEvents.map((e) => ({
      ...e,
      eventDate: e.eventDate instanceof Date ? e.eventDate : new Date(e.eventDate),
    }));
  });
  const [isLoading, setIsLoading] = useState(!initialEvents);

  useEffect(() => {
    if (initialEvents) return;

    let isMounted = true;
    async function loadEvents() {
      try {
        const res = await fetch('/api/events');
        if (res.ok && isMounted) {
          const data = await res.json();
          if (Array.isArray(data.events)) {
            setEvents(
              data.events.map((e: CommunityEvent) => ({
                ...e,
                eventDate: new Date(e.eventDate),
              }))
            );
          }
        }
      } catch (err) {
        // Fail gracefully
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadEvents();
    return () => {
      isMounted = false;
    };
  }, [initialEvents]);

  if (isLoading) {
    return (
      <section className="py-12 h-full px-8 bg-surface flex items-center justify-center min-h-[240px]">
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading events...</span>
        </div>
      </section>
    );
  }

  if (events.length === 0) {
    return null;
  }

  return (
    <section className="py-12 px-8 bg-surface">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4">
            Join Our Community
          </h2>
          <p className="text-lg text-foreground mb-6 max-w-2xl mx-auto">
            Connect with other job seekers at our upcoming events. Learn new skills, network with peers, and accelerate your career search.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
            />
          ))}
        </div>
      </div>
    </section>
  );
}